import React from "react";
import {
  Users as PartiesIcon,
  Calendar,
  Clock,
  Bell,
  RotateCw,
  AlertTriangle,
  Lightbulb,
  Sparkles,
  Gavel,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import RiskBadge from "./RiskBadge";
import { useLocaleFormat } from "../i18n/useLocaleFormat";

const KIND_TONE = {
  effective: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expiration: "bg-rose-50 text-rose-700 border-rose-200",
  renewal_notice: "bg-amber-50 text-amber-800 border-amber-200",
  deadline: "bg-indigo-50 text-indigo-700 border-indigo-200",
  payment_due: "bg-zinc-50 text-zinc-700 border-zinc-200",
  other: "bg-zinc-50 text-zinc-700 border-zinc-200",
};

const CAT_META = {
  notice: "bg-amber-50 text-amber-800",
  renewal: "bg-emerald-50 text-emerald-700",
  payment: "bg-indigo-50 text-indigo-700",
  compliance: "bg-rose-50 text-rose-700",
  reporting: "bg-zinc-50 text-zinc-700",
  other: "bg-zinc-50 text-zinc-600",
};

function Card({ title, Icon, accent, children, testId }) {
  return (
    <section data-testid={testId} className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 mb-5">
        <div className={`w-7 h-7 rounded-md grid place-items-center ${accent}`}>
          <Icon size={14} strokeWidth={1.75} />
        </div>
        <h2 className="font-display font-semibold text-base tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function DocumentView({ universal, enrichment, classification, EnrichmentPanel }) {
  const { t } = useTranslation("analysis");
  const { formatDate } = useLocaleFormat();
  const u = universal || {};
  const c = classification || {};
  const docTypeLabel = (c.type || "other").replace(/^./, (x) => x.toUpperCase());

  return (
    <div className="space-y-6" data-testid="document-view">
      {/* Classification banner */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-zinc-950 text-white" data-testid="classification-banner">
        <div className="flex items-center gap-2.5 min-w-0">
          <Sparkles size={14} className="text-white/70" strokeWidth={1.75} />
          <span className="text-sm font-medium" data-testid="classification-type">{docTypeLabel}</span>
          {c.sub_type && <span className="text-xs text-white/60" data-testid="classification-subtype">· {c.sub_type}</span>}
        </div>
        <span className="text-[11px] text-white/60" data-testid="classification-confidence">
          {typeof c.confidence === "number" ? t("sections.confidence", { percent: Math.round(c.confidence * 100) }) : ""}
        </span>
      </div>

      {/* Universal — Executive summary */}
      <section className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-7" data-testid="universal-summary-section">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={15} className="text-zinc-700" strokeWidth={1.75} />
          <h2 className="font-display font-semibold text-base tracking-tight">{t("sections.executive_summary")}</h2>
        </div>
        <p className="text-[15px] leading-relaxed text-zinc-700">{u.summary || t("empty.summary")}</p>
      </section>

      {/* Universal — Parties */}
      <Card title={t("sections.parties")} Icon={PartiesIcon} accent="bg-indigo-50 text-indigo-700" testId="universal-parties">
        {u.parties?.length ? (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="universal-parties-list">
            {u.parties.map((p, i) => (
              <li key={i} className="px-3 py-2 rounded-lg border border-zinc-200" data-testid={`universal-party-${i}`}>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500">{p.role}</p>
                <p className="text-sm font-semibold text-zinc-900 mt-0.5">{p.name}</p>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-zinc-500">{t("empty.parties")}</p>}
      </Card>

      {/* Universal — Key dates */}
      <Card title={t("sections.key_dates")} Icon={Calendar} accent="bg-emerald-50 text-emerald-700" testId="universal-key-dates">
        {u.key_dates?.length ? (
          <ul className="divide-y divide-zinc-100" data-testid="universal-key-dates-list">
            {u.key_dates.map((d, i) => {
              const tone = KIND_TONE[d.kind] || KIND_TONE.other;
              const label = t(`date_kind.${d.kind}`, t("date_kind.other"));
              return (
                <li key={i} className="flex items-center justify-between gap-3 py-2.5" data-testid={`universal-key-date-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">{d.label}</p>
                    <p className="text-xs text-zinc-500 tabular-nums">{formatDate(d.date)}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${tone}`}>
                    {label}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : <p className="text-sm text-zinc-500">{t("empty.key_dates")}</p>}
      </Card>

      {/* Universal — Renewal terms */}
      <Card title={t("sections.renewal_terms")} Icon={RotateCw} accent="bg-amber-50 text-amber-700" testId="universal-renewal-terms">
        <div className="grid sm:grid-cols-3 gap-4">
          <div data-testid="renewal-auto-renews">
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{t("fields.auto_renews")}</p>
            <div className="flex items-center gap-1.5">
              {u.renewal_terms?.auto_renews
                ? <><CheckCircle2 size={15} className="text-emerald-600" /><span className="text-sm font-semibold text-zinc-900">{t("fields.yes")}</span></>
                : <><XCircle size={15} className="text-zinc-400" /><span className="text-sm font-semibold text-zinc-900">{t("fields.no")}</span></>}
            </div>
          </div>
          <div data-testid="renewal-notice-window">
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{t("fields.notice_window")}</p>
            <p className="text-sm font-semibold text-zinc-900">
              {u.renewal_terms?.notice_window_days
                ? t("fields.days", { count: u.renewal_terms.notice_window_days })
                : "—"}
            </p>
          </div>
          <div data-testid="renewal-next-action">
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{t("fields.next_action_date")}</p>
            <p className="text-sm font-semibold text-zinc-900 tabular-nums">{u.renewal_terms?.next_action_date ? formatDate(u.renewal_terms.next_action_date) : "—"}</p>
          </div>
        </div>
      </Card>

      {/* Universal — Obligations */}
      <Card title={t("sections.obligations")} Icon={Gavel} accent="bg-zinc-50 text-zinc-700" testId="universal-obligations">
        {u.obligations?.length ? (
          <ul className="divide-y divide-zinc-100" data-testid="universal-obligations-list">
            {u.obligations.map((o, i) => (
              <li key={i} className="py-2.5" data-testid={`universal-obligation-${i}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm text-zinc-800 leading-relaxed">{o.what}</p>
                  {o.deadline && <span className="text-xs text-zinc-500 shrink-0 tabular-nums">{o.deadline}</span>}
                </div>
                <p className="text-[11px] text-zinc-500 mt-0.5">{t("fields.by_party", { party: o.party })}</p>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-zinc-500">{t("empty.obligations")}</p>}
      </Card>

      {/* Universal — Deadlines */}
      <Card title={t("sections.deadlines")} Icon={Bell} accent="bg-amber-50 text-amber-700" testId="universal-deadlines">
        {u.deadlines?.length ? (
          <ul className="divide-y divide-zinc-100" data-testid="universal-deadlines-list">
            {u.deadlines.map((d, i) => (
              <li key={i} className="py-2.5" data-testid={`universal-deadline-${i}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm text-zinc-900 font-medium">{d.what}</p>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${CAT_META[d.category] || CAT_META.other}`}>
                    {t(`deadline_category.${d.category}`, t("deadline_category.other"))}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">{d.when}</p>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-zinc-500">{t("empty.deadlines")}</p>}
      </Card>

      {/* Universal — Penalties */}
      {u.penalties?.length > 0 && (
        <Card title={t("sections.penalties")} Icon={AlertTriangle} accent="bg-rose-50 text-rose-700" testId="universal-penalties">
          <ul className="space-y-3">
            {u.penalties.map((p, i) => (
              <li key={i} data-testid={`universal-penalty-${i}`} className="text-sm leading-relaxed">
                <p className="font-medium text-zinc-900">{t("fields.penalty_if")} : <span className="font-normal">{p.trigger}</span></p>
                <p className="text-zinc-700 mt-0.5">{t("fields.penalty_then")} : {p.consequence}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Universal — Risks */}
      <Card title={t("sections.risks")} Icon={AlertTriangle} accent="bg-red-50 text-red-700" testId="universal-risks">
        {u.risks?.length ? (
          <ul className="space-y-3" data-testid="universal-risks-list">
            {u.risks.map((r, i) => (
              <li key={i} data-testid={`universal-risk-${i}`} className="flex items-start gap-3 text-sm leading-relaxed">
                <AlertTriangle
                  size={15}
                  strokeWidth={1.75}
                  className={`shrink-0 mt-0.5 ${
                    r.level === "High" ? "text-red-500" : r.level === "Medium" ? "text-amber-500" : "text-emerald-500"
                  }`}
                />
                <div className="min-w-0">
                  <p className="text-zinc-800">{r.text}</p>
                  <RiskBadge level={r.level} size="sm" />
                </div>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-zinc-500">{t("empty.risks")}</p>}
      </Card>

      {/* Universal — Recommended actions */}
      {u.recommended_actions?.length > 0 && (
        <Card title={t("sections.actions")} Icon={Lightbulb} accent="bg-amber-50 text-amber-700" testId="universal-actions">
          <ul className="space-y-3">
            {u.recommended_actions.map((act, i) => (
              <li key={i} data-testid={`universal-action-${i}`} className="flex gap-3 text-sm text-zinc-700 leading-relaxed">
                <span className="font-display font-semibold text-zinc-400 shrink-0 w-5">{String(i + 1).padStart(2, "0")}</span>
                <span>{act}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Layer 3 — Optional type-specific enrichment */}
      {EnrichmentPanel && enrichment && Object.keys(enrichment).length > 0 && (
        <section data-testid="enrichment-panel">
          <h3 className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3 px-1">
            {t("sections.type_specific_details", { type: docTypeLabel })}
          </h3>
          <EnrichmentPanel enrichment={enrichment} />
        </section>
      )}
    </div>
  );
}
